import { useEffect, useMemo, useRef, useState } from "react";
import {
  FestivalMainBannerButton,
  FestivalMainBannerHeader,
  FestivalMainBannerSlideWrapper,
  FestivalMainBannerTextWrapper,
  FestivalMainBannerTime,
  FestivalMainBannerTimeItem,
  FestivalMainBannerWrapper,
} from "./style";
import FestivalBannerTournament from "./tournament";
import { useRabbitFestivalContext } from "../../../../hooks/festival-context";
import RabbitFestivalMainBannerSkeleton from "./skeleton";
import { useHistory } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { insetState } from "../../../../../../../recoil/inset";

const RabbitFestivalMainBanner = () => {
  const { tournaments, mainLoading, setShowHeader, handleEventPage } =
    useRabbitFestivalContext();
  const bannerRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [fade, setFade] = useState(false);
  const inset = useRecoilValue(insetState);

  const isTournamentEnd = useMemo(
    () =>
      tournaments[currentIndex]?.isEnd || !tournaments[currentIndex]?.timeInfo,
    [tournaments, currentIndex]
  );

  useEffect(() => {
    if (!tournaments || tournaments.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % tournaments.length);
      setFade(true);

      setTimeout(() => {
        setFade(false);
      }, 100);
    }, 7000);

    return () => clearInterval(interval);
  }, [tournaments?.length]);

  const isButton = () => {
    if (tournaments[currentIndex]?.timeInfo?.elapsedTimeMs) {
      return tournaments[currentIndex]?.timeInfo?.elapsedTimeMs < 0;
    }
    if (!tournaments[currentIndex]?.timerDto) return true;
    return isTournamentEnd;
  };

  // header 투명 여부
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const target = entries[0];
        if (target.isIntersecting) {
          setShowHeader(true);
        } else {
          setShowHeader(false);
        }
      },
      {
        threshold: 0.5,
      }
    );

    if (bannerRef.current) {
      observer.observe(bannerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [bannerRef.current]);

  const getButtonText = () => {
    if (isButton()) {
      if (isTournamentEnd) return "최종 순위 확인하기";
      return tournaments[currentIndex]?.reservation
        ? "토너먼트 예약하기"
        : "시작 대기중";
    }
    return "";
  };

  const handleButton = () => {
    if (isButton() || tournaments[currentIndex]?.isEnd) {
      handleEventPage(tournaments[currentIndex]?.competition?.id || 0);
      return;
    }
    history.push(`/tournament/${tournaments[currentIndex]?.id}`);
  };

  const getTime = () => {
    if (!tournaments[currentIndex]?.timeInfo?.elapsedTimeMs)
      return { hours: "00", minutes: "00", seconds: "00" };
    let diff = Math.abs(tournaments[currentIndex]?.timeInfo?.elapsedTimeMs);

    const hours = Math.floor(diff / (1000 * 60 * 60));
    diff %= 1000 * 60 * 60;

    const minutes = Math.floor(diff / (1000 * 60));
    diff %= 1000 * 60;

    const seconds = Math.floor(diff / 1000);

    return {
      hours: hours.toString().padStart(2, "0"),
      minutes: minutes.toString().padStart(2, "0"),
      seconds: seconds.toString().padStart(2, "0"),
    };
  };

  const reservationCount = useMemo(() => {
    return tournaments.reduce((count, tournament) => {
      if (!tournament?.reservation) return count;

      // 인원 마감이거나 수동 마감인 경우
      if (
        (tournament.reservation.limitEntry &&
          tournament.reservation.reservationCount ===
            tournament.reservation.limitEntry) ||
        tournament.reservation.isEnd
      ) {
        return count;
      }

      const currentLevel = tournament?.timeInfo?.currentLevel.level;
      const level = currentLevel ?? tournament?.timeInfo?.nextLevel?.level - 1;

      // 레지마감 레벨
      if (tournament?.registerDeadlineLevel >= level) {
        return count + 1;
      }

      return count;
    }, 0);
  }, [tournaments]);

  if (mainLoading) {
    return <RabbitFestivalMainBannerSkeleton />;
  }

  return (
    <FestivalMainBannerWrapper ref={bannerRef}>
      {reservationCount ? (
        <FestivalMainBannerHeader inset={inset}>
          {reservationCount}개의 이벤트 예약 오픈
        </FestivalMainBannerHeader>
      ) : (
        <></>
      )}
      {tournaments.map((tournament, index) => (
        <FestivalMainBannerSlideWrapper
          key={index}
          isSelected={index === currentIndex}
          fade={fade}
        >
          <img src={tournament?.competition?.bannerImage} alt="event" />
          <FestivalMainBannerTextWrapper isTime={isButton()}>
            <div className="title">{tournament?.title}</div>
            <div className="sub-title">{tournament?.subTitle}</div>
            {isButton() && !isTournamentEnd && (
              <FestivalMainBannerTime>
                <FestivalMainBannerTimeItem>
                  <div className="time">{getTime().hours}</div>
                  <div className="unit">시간</div>
                </FestivalMainBannerTimeItem>
                <FestivalMainBannerTimeItem>
                  <div className="time">{getTime().minutes}</div>
                  <div className="unit">분</div>
                </FestivalMainBannerTimeItem>
                <FestivalMainBannerTimeItem>
                  <div className="time">{getTime().seconds}</div>
                  <div className="unit">초</div>
                </FestivalMainBannerTimeItem>
              </FestivalMainBannerTime>
            )}
          </FestivalMainBannerTextWrapper>
          <div className="back" />
        </FestivalMainBannerSlideWrapper>
      ))}

      <FestivalMainBannerButton onClick={handleButton}>
        {isButton() ? (
          <div className="button">{getButtonText()}</div>
        ) : (
          tournaments[currentIndex]?.timeInfo && (
            <FestivalBannerTournament tournament={tournaments[currentIndex]} />
          )
        )}
      </FestivalMainBannerButton>
    </FestivalMainBannerWrapper>
  );
};

export default RabbitFestivalMainBanner;
